/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useEffect } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InfoIcon from '@mui/icons-material/Info';
import { FileUploader } from 'react-drag-drop-files';
import { useDispatch } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import LayoutProcuration from 'src/Components/Procuration/LayoutProcuration';
import { getActivePrestataireBrandConfig } from 'src/config/environment.config';
import Functions from 'src/Helpers/Functions';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import ImageRepository from 'src/Repositories/ImageRepository';
import TeachrRepository from 'src/Repositories/TeachrRepository';
import Teachr from 'src/Objects/models/Teachr';
import { userTeachrSetAccountAction } from 'src/Redux/Actions/UserActions';
import { useProcurationContext } from 'src/Contexts/ProcurationContext';

const fileTypes = ["PDF", "PNG", "JPEG", "JPG"];

/**
 * Document déjà enregistré côté API (entité Image) : on le reconnaît à son
 * identifiant. Un fichier fraîchement choisi par le formateur est une instance
 * de File.
 */
interface StoredFile {
    '@id'?: string;
    id?: number;
    contentUrl?: string;
    filePath?: string;
}
type ProofFile = File | StoredFile | null;

const isLocalFile = (file: ProofFile): file is File => file instanceof File;
const isStoredFile = (value: unknown): value is StoredFile =>
    typeof value === 'object' && value !== null && !(value instanceof File)
    && (Boolean((value as StoredFile)['@id']) || Boolean((value as StoredFile).id));
const toStoredFile = (value: unknown): StoredFile | null => (isStoredFile(value) ? value : null);
/** Copie sérialisable pour le store Redux, qui est persisté en localStorage. */
const serializeStoredFile = (file: ProofFile): StoredFile | null =>
    (isStoredFile(file) ? JSON.parse(JSON.stringify(file)) : null);

interface FormState {
    nationality: number; // 1 = Française, 0 = Étrangère
    file: ProofFile;
    file2: ProofFile;
    error: string;
    error2: string;
    loading: boolean;
}

interface FileDisplayProps {
    file: File;
    subtitle: string;
    onRemove: () => void;
    classes: any;
}

const FileDisplay: React.FC<FileDisplayProps> = ({ file, subtitle, onRemove, classes }) => (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
            <DescriptionIcon className="text-blue-600 w-8 h-8" />
            <div>
                <p className={`${classes.fontFamily} font-semibold text-gray-900`}>
                    {file?.name}
                </p>
                <p className="text-sm text-gray-600">{subtitle}</p>
            </div>
            <CheckCircleIcon className="text-green-500 w-6 h-6" />
        </div>
        <button
            onClick={onRemove}
            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
            aria-label="Supprimer le fichier"
        >
            <DeleteIcon className="text-red-500" />
        </button>
    </div>
);

interface ExistingFileDisplayProps {
    file: StoredFile;
    label: string;
    classes: any;
}

/** Document déjà rattaché au compte : consultable et remplaçable, jamais supprimé depuis cet écran. */
const ExistingFileDisplay: React.FC<ExistingFileDisplayProps> = ({ file, label, classes }) => (
    <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-xl p-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
            <DescriptionIcon className={`w-8 h-8 flex-shrink-0 ${classes.primaryText}`} />
            <div className="min-w-0">
                <p className={`${classes.fontFamily} font-semibold text-gray-900`}>{label}</p>
                <p className="text-sm text-gray-600">Déjà enregistré sur votre compte</p>
                {file.contentUrl && (
                    <a
                        href={file.contentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-sm font-semibold ${classes.primaryText} hover:underline ${classes.fontFamily}`}
                    >
                        Voir le document
                    </a>
                )}
            </div>
        </div>
        <CheckCircleIcon className="text-green-500 w-6 h-6 flex-shrink-0" />
    </div>
);
interface FileUploadZoneProps {
    onFileChange: (file: File) => void;
    onTypeError: () => void;
    name: string;
    variant?: 'primary' | 'secondary';
    title?: string;
    description?: string;
    showButton?: boolean;
    classes: any;
    common: any;
    typeOfProcuration?: number
}
const FileUploadZone: React.FC<FileUploadZoneProps> = ({
    onFileChange,
    onTypeError,
    name,
    variant = 'primary',
    title,
    description,
    showButton = true,
    classes,
    common
}) => {
    const isPrimary = variant === 'primary';

    return (
        <FileUploader
            classes="focus-within:bg-blue-50"
            onTypeError={onTypeError}
            handleChange={onFileChange}
            name={name}
            types={fileTypes}
        >
            <div className={`flex ${isPrimary ? 'flex-col' : 'items-center justify-center'} gap-4 ${isPrimary ? 'p-8' : 'p-6'} border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 transition-colors cursor-pointer ${isPrimary ? 'bg-gray-50' : ''}`}>
                <div className='flex gap-2 items-center justify-around'>
                    <AttachFileIcon className={`text-gray-400 ${isPrimary ? 'w-12 h-12' : 'w-8 h-8'}`} />
                    {isPrimary ? (
                        <div className="/text-center ">
                            <p className={`${common.body} text-gray-700 mb-1`}>
                                {title || 'Ajoute ton document d\'identité'}
                            </p>
                            <p className="text-sm text-gray-500">
                                {description || 'Format: PDF, JPEG ou PNG. - Poids: 5mo maximum.'}
                            </p>
                        </div>
                    ) : (
                        <span className={`text-sm font-semibold ${classes.primaryText} underline`}>
                            {title || 'Choisissez un fichier'}
                        </span>
                    )}
                </div>
                {showButton && isPrimary && (
                    <button className="px-6 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm">
                        Charger un document
                    </button>
                )}
            </div>
        </FileUploader>
    );
};
interface ErrorDisplayProps {
    error: string;
    classes: any;
}
const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, classes }) => (
    error ? (
        <div className={`bg-red-50 border border-red-200 rounded-lg p-4`}>
            <p className={`text-sm ${classes.error} ${classes.fontFamily} font-medium`}>{error}</p>
        </div>
    ) : null
);

interface ProofUploadResult {
    teachr: Teachr;
    image: StoredFile;
}

/**
 * Envoie un justificatif à l'API et le rattache au compte du formateur
 * (proofIdentity ou proofIdentity2). La mise à jour du store de procuration est
 * faite par l'appelant une fois tous les fichiers envoyés, pour que le
 * récapitulatif reflète exactement les documents affichés sur cet écran.
 */
const uploadProofIdentity = async (
    file: File,
    currentTeachr: Teachr,
    dispatch: Dispatch<AnyAction>,
    proof: number,
    typeOfProcuration?: number
): Promise<ProofUploadResult> => {
    const label = (typeOfProcuration === 20 ? 'proof-is-sign-sap-1' : (proof === 1 ? 'proof-is-ae-identity' : 'proof-is-ae-identity2'));
    // Préserver le type de fichier original au lieu de forcer "application/pdf"
    const fileToUpload = new File([file], label, { type: file.type || "application/pdf" });

    try {
        const response = await ImageRepository.createImage(fileToUpload);

        const updatedTeachr = { ...currentTeachr };

        // Convertir TOUS les objets en références IRI avant la mise à jour
        // C'est crucial pour éviter l'erreur "Nested documents are not allowed"
        if (updatedTeachr.proofIdentity && typeof updatedTeachr.proofIdentity === 'object') {
            updatedTeachr.proofIdentity = updatedTeachr.proofIdentity["@id"];
        }
        if (updatedTeachr.proofIdentity2 && typeof updatedTeachr.proofIdentity2 === 'object') {
            updatedTeachr.proofIdentity2 = updatedTeachr.proofIdentity2["@id"];
        }
        if (updatedTeachr.proofSignSap && typeof updatedTeachr.proofSignSap === 'object') {
            updatedTeachr.proofSignSap = updatedTeachr.proofSignSap["@id"];
        }
        if (updatedTeachr.proofIsAE && typeof updatedTeachr.proofIsAE === 'object') {
            updatedTeachr.proofIsAE = updatedTeachr.proofIsAE["@id"];
        }
        if (updatedTeachr.proofMandat && typeof updatedTeachr.proofMandat === 'object') {
            updatedTeachr.proofMandat = updatedTeachr.proofMandat["@id"];
        }

        // Maintenant, mettre à jour le champ approprié avec le nouvel IRI
        if (typeOfProcuration === 20) {
            updatedTeachr.proofIdentity = response['@id'];
        } else if (proof === 1) {
            updatedTeachr.proofIdentity = response["@id"];
        } else if (proof === 2) {
            updatedTeachr.proofIdentity2 = response["@id"];
        }

        const teachrResponse = await TeachrRepository.updateTeachr(updatedTeachr);

        const serializedTeachr = JSON.parse(JSON.stringify(teachrResponse));
        dispatch(userTeachrSetAccountAction(serializedTeachr));
        dispatch({ type: 'SET_TEACHR_INFO', payload: { teachr: serializedTeachr } });

        return { teachr: serializedTeachr, image: JSON.parse(JSON.stringify(response)) };
    } catch (error: any) {
        console.warn('Erreur lors de l\'upload:', error);

        const errorMessage = error?._description === "Invalid filename provided"
            ? "Le format du fichier est invalide. Formats acceptés : PDF, PNG, JPEG, JPG"
            : "Une erreur est survenue lors de l'upload du fichier. Veuillez réessayer.";

        dispatch({
            type: "SHOW_ERROR",
            payload: errorMessage,
        });

        throw error; // Re-throw pour permettre la gestion d'erreur en cascade
    }
}

const SecondProcuration = () => {
    const dispatch = useDispatch();
    const context = useProcurationContext();
    const { teachr, generalProcuration, nextStep, prevStep, typeOfProcuration } = context;
    const { classes, common } = useTailwindBrand();
    const isAe = typeOfProcuration === 30;

    // Documents déjà connus : ceux de la procuration en cours (store), sinon
    // ceux déjà rattachés au compte. Un formateur qui recommence sa procuration
    // retrouve ainsi sa pièce d'identité au lieu de devoir la renvoyer, tout en
    // gardant la possibilité de la remplacer.
    const existingFile: StoredFile | null =
        toStoredFile(isAe ? generalProcuration?.thirdProc?.file : generalProcuration?.third?.file)
        ?? toStoredFile(teachr?.proofIdentity);
    const existingFile2: StoredFile | null = isAe
        ? (toStoredFile(generalProcuration?.thirdProc?.file2) ?? toStoredFile(teachr?.proofIdentity2))
        : null;

    const [formState, setFormState] = useState<FormState>({
        nationality: generalProcuration?.secondProc?.nationality === 73 ? 1 : 0,
        file: existingFile,
        file2: existingFile2,
        error: '',
        error2: '',
        loading: false
    });

    // Si le compte est rafraîchi après le montage, on complète les emplacements
    // encore vides sans écraser un fichier choisi entre-temps.
    useEffect(() => {
        setFormState(prev => {
            const file = prev.file ?? existingFile;
            const file2 = prev.file2 ?? existingFile2;
            return file === prev.file && file2 === prev.file2 ? prev : { ...prev, file, file2 };
        });
    }, [existingFile, existingFile2]);

    const setLoading = (value: boolean) => {
        setFormState(prev => ({ ...prev, loading: value }));
    }

    const processFile = useCallback(async (file: File, isSecond: boolean = false) => {
        if (file.type.search("image") === 0) {
            const fileType = file.type.split('/');
            try {
                const resizedFile = await Functions.resizeFile(file, true, fileType[1]) as File;
                if (isSecond) {
                    setFormState(prev => ({ ...prev, file2: resizedFile, error2: '' }));
                } else {
                    setFormState(prev => ({ ...prev, file: resizedFile, error: '' }));
                }
            } catch (error) {
                console.warn(error);
                setFormState(prev => ({
                    ...prev,
                    [isSecond ? 'error2' : 'error']: 'Erreur lors du traitement de l\'image'
                }));
            }
        } else {
            if (isSecond) {
                setFormState(prev => ({ ...prev, file2: file, error2: '' }));
            } else {
                setFormState(prev => ({ ...prev, file: file, error: '' }));
            }
        }
    }, []);

    const handleFileChange = useCallback((file: File) => {
        processFile(file, false);
    }, [processFile]);

    const handleFile2Change = useCallback((file: File) => {
        processFile(file, true);
    }, [processFile]);

    // const handleNationalityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //     const value = parseInt(event.target.value);
    //     setFormState(prev => ({ ...prev, nationality: value }));
    // };

    // Retirer un fichier fraîchement choisi ramène au document déjà enregistré
    // s'il existe : on ne supprime jamais un justificatif du compte depuis cet écran.
    const removeFile = (isSecond: boolean = false) => {
        if (isSecond) {
            setFormState(prev => ({ ...prev, file2: existingFile2, error2: '' }));
        } else {
            setFormState(prev => ({ ...prev, file: existingFile, error: '' }));
        }
    };

    const nextPage = async () => {
        const mainFile = formState.file;
        const secondFile = formState.file2;

        if (!mainFile) {
            setFormState(prev => ({
                ...prev,
                error: 'Veuillez ajouter un document d\'identité avant de continuer.'
            }));
            return;
        }

        setFormState(prev => ({ ...prev, error: '', error2: '' }));

        // Documents à mémoriser dans le store : ceux déjà enregistrés tels quels,
        // les nouveaux une fois envoyés à l'API.
        let storedFile1: StoredFile | null = isLocalFile(mainFile) ? null : serializeStoredFile(mainFile);
        let storedFile2: StoredFile | null = isLocalFile(secondFile) ? null : serializeStoredFile(secondFile);

        if (isLocalFile(mainFile) || isLocalFile(secondFile)) {
            setLoading(true);
            try {
                // Chaque envoi repart du compte renvoyé par le précédent pour ne pas
                // écraser proofIdentity avec une version périmée.
                let currentTeachr = teachr;
                if (isLocalFile(mainFile)) {
                    const result = await uploadProofIdentity(mainFile, currentTeachr, dispatch, 1, typeOfProcuration);
                    currentTeachr = result.teachr;
                    storedFile1 = result.image;
                }
                if (isLocalFile(secondFile)) {
                    const result = await uploadProofIdentity(secondFile, currentTeachr, dispatch, 2, typeOfProcuration);
                    currentTeachr = result.teachr;
                    storedFile2 = result.image;
                }
            } catch (error) {
                console.error('Erreur lors de l\'upload des fichiers:', error);
                setLoading(false);
                return;
            }
            setLoading(false);
        }

        if (isAe) {
            const payload = {
                step: 4,
                secondProc: generalProcuration?.secondProc || {},
                thirdProc: { file: storedFile1, file2: storedFile2 },
                fourthProc: generalProcuration?.fourthProc || {},
                fifthProc: generalProcuration?.fifthProc || {},
                sixthProc: generalProcuration?.sixthProc || {},
                legalRepProc: generalProcuration?.legalRepProc || [],
                seventhProc: generalProcuration?.seventhProc || {},
                eightProc: generalProcuration?.eightProc || {}
            };
            dispatch({ type: 'SET_AE_INFO', payload });
        } else {
            const payload = {
                step: 4,
                first: generalProcuration?.first || {},
                second: generalProcuration?.second || {},
                third: { file: storedFile1 },
                fourth: generalProcuration?.fourth || {},
            };
            dispatch({ type: 'SET_SAP_INFO', payload });
        }
        nextStep(isAe ? 3 : 4);
    };

    const isFrench = formState.nationality === 1;
    const documentLabel = isFrench
        ? 'Carte d\'identité ou passeport'
        : 'Titre de séjour ou carte de résident';

    const mainDocumentLabel = typeOfProcuration === 20 ? 'Avis de situation ou extrait KBIS' : documentLabel;

    const mainUploadZone = (
        <FileUploadZone
            onFileChange={handleFileChange}
            onTypeError={() => setFormState(prev => ({ ...prev, error: 'Format invalide. Formats acceptés : PDF, PNG, JPEG, JPG' }))}
            name="file"
            variant="primary"
            classes={classes}
            common={common}
            title={typeOfProcuration === 20 ? `Ajouter votre avis de situation ou extrait KBIS` : ''}
        />
    );
    const secondUploadZone = (
        <FileUploadZone
            onFileChange={handleFile2Change}
            onTypeError={() => setFormState(prev => ({ ...prev, error2: 'Format invalide' }))}
            name="file2"
            variant="secondary"
            classes={classes}
            common={common}
            showButton={false}
        />
    );

    // Document principal : fichier fraîchement choisi, sinon document déjà
    // enregistré avec une zone pour le remplacer, sinon zone d'envoi seule.
    const renderMainFile = () => {
        const mainFile = formState.file;
        if (isLocalFile(mainFile)) {
            return (
                <FileDisplay
                    file={mainFile}
                    subtitle="Fichier téléchargé avec succès"
                    onRemove={() => removeFile(false)}
                    classes={classes}
                />
            );
        }
        if (mainFile) {
            return (
                <div className="space-y-3">
                    <ExistingFileDisplay file={mainFile} label={mainDocumentLabel} classes={classes} />
                    <p className={`text-xs text-center text-gray-500 ${common.body}`}>Ou remplacer par un nouveau fichier</p>
                    {mainUploadZone}
                </div>
            );
        }
        return mainUploadZone;
    };

    // Fichier supplémentaire facultatif, même logique que le document principal.
    const renderSecondFile = () => {
        const secondFile = formState.file2;
        if (isLocalFile(secondFile)) {
            return (
                <FileDisplay
                    file={secondFile}
                    subtitle="Fichier supplémentaire"
                    onRemove={() => removeFile(true)}
                    classes={classes}
                />
            );
        }
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-3">
                {secondFile ? (
                    <>
                        <ExistingFileDisplay file={secondFile} label="Document supplémentaire" classes={classes} />
                        <p className={`text-xs text-center text-gray-500 ${common.body}`}>Ou remplacer par un nouveau fichier</p>
                    </>
                ) : (
                    <p className={`${common.body} font-semibold mb-4 ${classes.text}`}>
                        Voulez-vous ajouter un fichier supplémentaire ?
                    </p>
                )}
                {secondUploadZone}
                <ErrorDisplay error={formState.error2} classes={classes} />
            </div>
        );
    };
   
    
    return (
        <LayoutProcuration
            title={`Annexe 1 :  ${typeOfProcuration === 30 ? `Document à joindre` : `Fiche d’information NOVA`}`}
            icon={<AttachFileIcon className={`w-6 h-6 ${classes.primaryText}`} />}
            prevLabel="Précédent"
            nextLabel={'Enregistrer et continuer'}
            nextStep={nextPage}
            prevStep={prevStep}
            loading={formState.loading}
        >
            <p className={`${classes.text} ${classes.fontFamily} md:text-base text-sm mb-4`}>
                {typeOfProcuration === 30 ?
                    `Nous vérifions tous les documents fournis pour que ${getActivePrestataireBrandConfig('s')} soit un endroit sûr.`
                    : `Veuillez prendre en compte les informations suivantes avant la signature du document.`}
                {/* <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${typeOfProcuration === 2 ? 'inline-flex' : 'hidden'} items-center gap-2 ${classes.primaryText} font-semibold hover:underline transition-colors text-center underline cursor-pointer`}
                    onClick={triggerPdfUpdate}
                    href={file}
                    download
                >
                    {" "}visualiser la procuration.
                </a> */}
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <InfoIcon className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className={`${classes.primaryText} ${classes.fontFamily} text-sm`}>
                    <p className={` mb-2`}>
                        {typeOfProcuration === 30 ?
                            <>
                                <span className="font-bold">{documentLabel}</span> toujours en cours de validité,
                                numérisée avec la mention manuscrite suivante :
                            </> :
                            <>
                                Merci de bien vouloir joindre votre extrait KBIS ou votre avis de situation SIREN.
                                Si vous n'avez plus vous pouvez le retélécharger depuis
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`${typeOfProcuration === 20 ? 'inline-flex' : 'hidden'} items-center gap-2 ${classes.primaryText} font-semibold hover:underline transition-colors text-center underline cursor-pointer`}
                                    href='https://avis-situation-sirene.insee.fr/'
                                >
                                    https://avis-situation-sirene.insee.fr/
                                </a>
                            </>
                        }
                    </p>
                    {/* <p className={` italic`}>
                        "j'atteste sur l'honneur que ce document est conforme à l'original"
                    </p> */}
                </div>
            </div>

            <div className="space-y-6">
                <div className="space-y-4">
                    {renderMainFile()}
                    {/* Fichier supplémentaire, proposé une fois le document principal fourni */}
                    {isAe && formState.file && renderSecondFile()}
                </div>

                <ErrorDisplay error={formState.error} classes={classes} />
            </div>
        </LayoutProcuration>
    );
};

export default SecondProcuration;
